import {
  exportToExcelMultiSheet,
  generateMultiSheetTemplate,
  ExcelColumn,
} from "./excelUtils";
import { normalizeBackfillType } from "./backfillTypes";
import {
  STORM_EO_CONC_BED_ITEMS,
  STORM_EO_ITEMS,
  STORM_REDUNDANT_SEWER_ITEMS,
  STORM_HYDROBRAKE_PENSTOCK_WEIR_ITEMS,
  STORM_FILTER_DRAIN_ITEMS,
} from "./stormSummaryUtils";
import {
  Manhole,
  StormSewerRun,
  LateralConnection,
  GullyConnection,
  FoulBuildUpRowData,
  FoulRoadConstConfig,
} from "../types/tender.types";
import { getPondConfigurableField, MAX_POND_SECTIONS, makePondRows } from './stormBuildUpPonds';
import { getSwaleConfigurableField, MAX_SWALE_SECTIONS, makeSwaleRows } from './stormBuildUpSwales';
import { HEADWALL_ROWS } from './stormBuildUpHeadwalls';
import { StormBuildUpRowDef } from './stormBuildUpTypes';
import { MAX_CELLULAR_SECTIONS, CELLULAR_CRATE_FIELDS, CELLULAR_SUB_CRATE_FIELDS } from './stormBuildUpCellularAttenuation';
import { getConcConfigurableField, CONC_DEFAULT_DRAWING_REF } from './stormBuildUpConcAttenuation';
import { CONC_SUPPLY_OPTIONS } from './stormConcAttenuationEvaluateCodesMapping';
import { getWeholiteConfigurableField } from './stormBuildUpWeholiteAttenuation';
import { MAX_BOX_CULVERT_SECTIONS, getBoxCulvertConfigurableField } from './stormBuildUpBoxCulverts';

export interface StormSewersExcelData {
  manholes: Manhole[];
  pipeRuns: StormSewerRun[];
  laterals: LateralConnection[];
  gullies: GullyConnection[];
  buildUp?: Record<string, FoulBuildUpRowData>;
  roadConstConfig?: FoulRoadConstConfig;
  manholeScheduleConfig?: { ladders: 'N/A' | 'Stainless' | 'Galvanised'; safetyChains: 'N/A' | 'Stainless' | 'Galvanised' };
  // Per-pond "Top soil spread, xxxmm thick" overrides read straight off the
  // sheet's own description cells — see parsePondsSwalesSheet below, same
  // convention as posExcelUtils.ts's rowThicknessMm.
  pondsSwalesConfig?: { pondTopsoilThicknessMm?: Record<string, number>; swaleTopsoilThicknessMm?: Record<string, number>; sectionTitles?: Record<string, string> };
  // Per-culvert height read straight off the sheet's own "x mm Box Culvert -
  // xxxm dp" sub-header cell (col C) — see parseBoxCulvertsSheet below, same
  // convention as pondsSwalesConfig above.
  boxCulvertsConfig?: { culvertConfigMm?: Record<string, number>; culvertBackfillType?: Record<string, string> };
  // Per-tank "Add xxx% FP/OHP on above" overrides read straight off the
  // sheet's own description cells — see parseConcAttenuationSheet above.
  concAttenuationConfig?: { concConfigPct?: Record<string, number>; drawingRefs?: Record<string, string> };
  // Per-tank "Add xxx% FP/OHP on above" overrides read straight off the
  // sheet's own description cells — see parseWeholiteAttenuationSheet below,
  // same convention as concAttenuationConfig above.
  weholiteAttenuationConfig?: { weholiteConfigPct?: Record<string, number> };
  // Per-tank crate size (m) read from the "Supply & Fit <L>m x <W>m x <H>m Crates" row
  // wording — see parseCellularAttenuationSheet.
  cellularAttenuationConfig?: { crateDims?: Record<string, number> };
  // Manual-input quantities read from the 'Storm Summary' sheet — see
  // parseStormSummarySheet below for which rows these can come from.
  eoQtys?: Record<string, number>;
}

// ─── Build-up parsing (Ponds & Swales sheet) ──────────────────────────────────

interface PondsSwalesRowMatchDef {
  id: string;
  description: string;
}

interface PondsSwalesSectionDef {
  trigger: RegExp;
  rowDefs: PondsSwalesRowMatchDef[];
  /** Header row id and the pattern whose group 1 is the user's appended title text ("POND 2 - NORTH" / "POND 2 NORTH"). */
  titleHeaderId?: string;
  titleRe?: RegExp;
}

// Configurable rows (the pond/swale "Top soil spread" rows, and the
// exTopsoilMm-driven "Excavate to reduce levels to take up ...mm top soil"
// row) carry a real mm value in the sheet where the app's own row def still
// has its unconfigured "xxx"/default-300 text — normalize both sides to the
// same token before comparing descriptions so those rows still match, same
// convention as posExcelUtils.ts's normalizeDesc.
const normalizeDesc = (s: string): string =>
  s.trim().toLowerCase().replace(/(?:\d+(?:\.\d+)?|x+)\s*mm/gi, '#mm');

const toRowDefs = (rows: StormBuildUpRowDef[]): PondsSwalesRowMatchDef[] =>
  rows.filter((r) => !r.isHeader).map((r) => ({ id: r.id, description: r.description }));

const makePondSectionDef = (n: number): PondsSwalesSectionDef => ({
  trigger: new RegExp(`^POND\\s*${n}(?:\\s+.*)?$`, 'i'),
  rowDefs: toRowDefs(makePondRows(n)),
  titleHeaderId: `pond_${n}_hdr`,
  titleRe: new RegExp(`^POND\\s*${n}(?!\\d)\\s*(?:-\\s*)?(\\S.*)$`, 'i'),
});

const makeSwaleSectionDef = (n: number): PondsSwalesSectionDef => ({
  trigger: new RegExp(`^SWALE\\s*${n}(?:\\s+.*)?$`, 'i'),
  rowDefs: toRowDefs(makeSwaleRows(n)),
  titleHeaderId: `swale_${n}_hdr`,
  titleRe: new RegExp(`^SWALE\\s*${n}(?!\\d)\\s*(?:-\\s*)?(\\S.*)$`, 'i'),
});

const PONDS_SWALES_SECTION_DEFS: PondsSwalesSectionDef[] = [
  ...Array.from({ length: MAX_POND_SECTIONS }, (_, i) => makePondSectionDef(i + 1)),
  {
    trigger: /^HEADWALLS?$/i,
    rowDefs: toRowDefs(HEADWALL_ROWS),
  },
  ...Array.from({ length: MAX_SWALE_SECTIONS }, (_, i) => makeSwaleSectionDef(i + 1)),
];

const isStormBuildUpHeaderRow = (desc: string): boolean =>
  /^(description|item|ref|quant|qty|unit|evaluate|code)\b/i.test(desc);

// Matches each sheet row to a field by its **description**, searched forward
// from the last match — not by position — so a row the sheet omits entirely
// (or one whose neighbours differ pond-to-pond, e.g. "Load, cart and bund
// excavated material") doesn't shift every later row in the section onto the
// wrong field id. Same fix as parsePOSSheet (posExcelUtils.ts) applied to a
// previous row-shift bug there.
const parsePondsSwalesSheet = (
  rows: unknown[][],
  descCol: number,
  quantCol: number,
  evaluateCol: number,
): { table: Record<string, FoulBuildUpRowData>; pondTopsoilThicknessMm: Record<string, number>; swaleTopsoilThicknessMm: Record<string, number>; sectionTitles: Record<string, string> } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const pondTopsoilThicknessMm: Record<string, number> = {};
  const swaleTopsoilThicknessMm: Record<string, number> = {};
  const sectionTitles: Record<string, string> = {};
  let currentDefs: PondsSwalesRowMatchDef[] = [];
  let pointer = 0;
  const triggeredSections = new Set<number>();

  for (const row of rows) {
    const desc = String(row[descCol] ?? '').trim();
    if (!desc) continue;
    if (isStormBuildUpHeaderRow(desc)) continue;

    const sectionIdx = PONDS_SWALES_SECTION_DEFS.findIndex(
      (sd, idx) => sd.trigger.test(desc) && !triggeredSections.has(idx)
    );
    if (sectionIdx !== -1) {
      triggeredSections.add(sectionIdx);
      currentDefs = PONDS_SWALES_SECTION_DEFS[sectionIdx].rowDefs;
      pointer = 0;
      const sectionDef = PONDS_SWALES_SECTION_DEFS[sectionIdx];
      const titleMatch = sectionDef.titleRe ? desc.match(sectionDef.titleRe) : null;
      if (sectionDef.titleHeaderId && titleMatch && titleMatch[1].trim()) sectionTitles[sectionDef.titleHeaderId] = titleMatch[1].trim();
      continue;
    }

    if (currentDefs.length === 0) continue;

    const normalizedDesc = normalizeDesc(desc);
    let matchIndex = -1;
    for (let i = pointer; i < currentDefs.length; i++) {
      if (normalizeDesc(currentDefs[i].description) === normalizedDesc) {
        matchIndex = i;
        break;
      }
    }
    if (matchIndex === -1) continue; // no remaining item in this section describes this row

    const id = currentDefs[matchIndex].id;
    pointer = matchIndex + 1;

    // Rows whose static description carries an "xxx" placeholder thickness
    // (currently just the pond/swale "Top soil spread" row) get their real
    // mm value read straight off the sheet's own description cell — same
    // convention as posExcelUtils.ts's rowThicknessMm.
    const pondConfigurableField = getPondConfigurableField(id);
    if (pondConfigurableField) {
      const mmMatch = desc.match(/(\d+(?:\.\d+)?)\s*mm/i);
      if (mmMatch) {
        const mm = Math.round(parseFloat(mmMatch[1]));
        if (mm > 0) pondTopsoilThicknessMm[id] = mm;
      }
    }
    const swaleConfigurableField = getSwaleConfigurableField(id);
    if (swaleConfigurableField) {
      const mmMatch = desc.match(/(\d+(?:\.\d+)?)\s*mm/i);
      if (mmMatch) {
        const mm = Math.round(parseFloat(mmMatch[1]));
        if (mm > 0) swaleTopsoilThicknessMm[id] = mm;
      }
    }

    const rawQuant = row[quantCol];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const q = parseFloat(qStr);
    if (isNaN(q)) continue;

    let evaluateCode = '';
    if (evaluateCol >= 0) {
      const rawEval = String(row[evaluateCol] ?? '').trim();
      evaluateCode = rawEval.startsWith('#') ? '' : rawEval;
    }

    result[id] = { quant: q, evaluateCode };
  }
  return { table: result, pondTopsoilThicknessMm, swaleTopsoilThicknessMm, sectionTitles };
};

// ─── Cellular Attenuation build-up parsing ────────────────────────────────────

const CELLULAR_TANK_TRIGGERS = [
  { tank: 1, pattern: /^Cellular\s+Tank\s*1$/i },
  ...Array.from({ length: MAX_CELLULAR_SECTIONS - 1 }, (_, i) => ({
    tank: i + 2,
    pattern: new RegExp(`^Tank\\s*${i + 2}$`, 'i'),
  })),
];

const CELLULAR_ROW_DEFS = [
  { suffix: 'excCart',       pattern: /^Excavate\s+&\s+Cart\s+Onsite/i },
  { suffix: 'levelComp',     pattern: /^Level\s+&\s+Compact\s+Formation/i },
  { suffix: 'earthwork',     pattern: /^Earthwork\s+Support/i },
  { suffix: 'terram',        pattern: /^Terram\s+surround/i },
  { suffix: 'membrane',      pattern: /^1mm\s+thick/i },
  { suffix: 'sandBed',       pattern: /^Sand\s+Bed\s+&\s+Surround/i },
  // Checked before the plain 'supplyFit' pattern below — both share the same
  // "Supply & Fit ... Crates" prefix, so the more specific "- Sub Contract"
  // suffix must be matched first or every sheet row would resolve to 'supplyFit'.
  { suffix: 'supplyFitSub',  pattern: /^Supply\s+&\s+Fit\s+.+?\s+Crates\s+-\s+Sub\s+Contract/i },
  { suffix: 'supplyFit',     pattern: /^Supply\s+&\s+Fit/i },
  { suffix: 'backfillCrush', pattern: /^Backfill\s+Crush/i },
  { suffix: 'backfillSoft',  pattern: /^Backfill\s+as\s+dug/i },
  { suffix: 'cart',          pattern: /^Cart\s+to\s+be\s+added/i },
];

const parseCellularAttenuationSheet = (
  rows: unknown[][],
  descCol: number,
  quantCol: number,
  evaluateCol: number,
): { table: Record<string, FoulBuildUpRowData>; crateDims: Record<string, number> } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const crateDims: Record<string, number> = {};
  let currentTank = -1;
  const triggeredTanks = new Set<number>();

  for (const row of rows) {
    const desc = String(row[descCol] ?? '').trim();
    if (!desc) continue;

    const tankTrigger = CELLULAR_TANK_TRIGGERS.find(
      (t) => t.pattern.test(desc) && !triggeredTanks.has(t.tank)
    );
    if (tankTrigger) {
      triggeredTanks.add(tankTrigger.tank);
      currentTank = tankTrigger.tank;
      continue;
    }

    if (currentTank === -1) continue;

    const rowDef = CELLULAR_ROW_DEFS.find((d) => d.pattern.test(desc));
    if (!rowDef) continue;

    // "Supply & Fit 2m x 0.5m x 0.5m Crates" — pick the crate size off the wording
    // (even when the row has no quantity) so a non-default size survives the import.
    if (rowDef.suffix === 'supplyFit' || rowDef.suffix === 'supplyFitSub') {
      const dims = desc.match(/Fit\s+(\d+(?:\.\d+)?)\s*m\s*x\s*(\d+(?:\.\d+)?)\s*m\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
      if (dims) {
        (rowDef.suffix === 'supplyFitSub' ? CELLULAR_SUB_CRATE_FIELDS : CELLULAR_CRATE_FIELDS).forEach((f, i) => {
          const v = parseFloat(dims[i + 1]);
          if (v > 0 && v !== f.default) crateDims[`ca_${currentTank}_${f.suffix}`] = v;
        });
      }
    }

    const rawQuant = row[quantCol];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const q = parseFloat(qStr);
    if (isNaN(q)) continue;

    let evaluateCode = '';
    if (evaluateCol >= 0) {
      const rawEval = String(row[evaluateCol] ?? '').trim();
      evaluateCode = rawEval.startsWith('#') ? '' : rawEval;
    }

    result[`ca_${currentTank}_${rowDef.suffix}`] = { quant: q, evaluateCode };
  }
  return { table: result, crateDims };
};

// ─── Weholite Attenuation build-up parsing ────────────────────────────────────
//
// The 'Weholite Attenuation' sheet has 5 identical tank blocks. Each block is
// introduced by the label "WEHOLITE ATTENUATION TANK" in the description column
// (col B). Between that header and the first real line item ("Excavate pit"),
// the sheet carries metadata rows ("Tank Dimensions", "Average Cover Level",
// "Average Invert Level", "Average EGL", "Post Strip"). Tank 1 additionally
// repeats "WEHOLITE ATTENUATION TANK" twice (sheet title + block header).
//
// A positional row-index approach mis-maps every quantity because those metadata
// rows shift the alignment. Instead we match each line item by its description
// label (like the Box Culverts parser). Labels that repeat within a block
// ("Bed and surround - side" ×2, "- end" ×2, "M&J R+S gang - Install" ×2) are
// disambiguated by their occurrence order, reset at each tank trigger.
// See map.md for the canonical field-to-cell mapping this mirrors.

interface WeholiteRowDef {
  suffix: string;
  pattern: RegExp;
}

// Ordered so that repeated labels resolve to the correct occurrence index.
const WEHOLITE_ROW_DEFS: WeholiteRowDef[] = [
  { suffix: 'excPit',       pattern: /^Excavate pit \(all-in\)$/i },
  { suffix: 'levelComp',    pattern: /^Level and compact$/i },
  { suffix: 'granBase',     pattern: /^Granular base$/i },
  { suffix: 'supplyTank',   pattern: /^Supply of Weholite Tank$/i },
  { suffix: 'welding',      pattern: /^Weholite welding$/i },
  { suffix: 'carriage',     pattern: /^Weholite carriage$/i },
  // Same "Add <pct>% FP/OHP on above" convention as Conc Attenuation's
  // fp5/ohp15 rows — matched generically (digits or the app's own "xxx"
  // placeholder) so the real configured value still resolves to this row.
  { suffix: 'fixedPrice',   pattern: /^Add (?:\d+(?:\.\d+)?|xxx)% FP on above$/i },
  { suffix: 'ohp',          pattern: /^Add (?:\d+(?:\.\d+)?|xxx)% OHP on above$/i },
  { suffix: 'offload21T',   pattern: /^Offloading - 21T operated excavator$/i },
  { suffix: 'offload2men',  pattern: /^Offloading - 2 men$/i },
  { suffix: 'install1',     pattern: /^M&J R\+S gang - Install$/i },
  { suffix: 'install2',     pattern: /^M&J R\+S gang - Install$/i },
  { suffix: 'totalBed',     pattern: /^Total Bed & Surround$/i },
  { suffix: 'asDug',        pattern: /^As dug backfill to void$/i },
  { suffix: 'stockpile',    pattern: /^Stockpile surplus onsite$/i },
  { suffix: 'foundCover',   pattern: /^Foundation for cover slab$/i },
  { suffix: 'coverSlab',    pattern: /^1200mm.? cover slab$/i },
  { suffix: 'seatingRings', pattern: /^Seating Rings$/i },
  { suffix: 'cf',           pattern: /^C\+F$/i },
];

const WEHOLITE_TRIGGER = /^WEHOLITE ATTENUATION TANK$/i;

const parseWeholiteAttenuationSheet = (
  rows: unknown[][],
  descCol: number,
  quantCol: number,
  evaluateCol: number,
): { table: Record<string, FoulBuildUpRowData>; weholiteConfigPct: Record<string, number> } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const weholiteConfigPct: Record<string, number> = {};
  let currentTank = 0;
  // Tracks how many times each suffix's pattern has matched within the current
  // tank, so duplicate labels map to the correct occurrence (install1/install2…).
  let occurrences: Record<string, number> = {};

  for (const row of rows) {
    const desc = String(row[descCol] ?? '').trim();
    if (!desc) continue;

    // A new tank header. Tank 1 shows this twice (title + block header); the
    // second occurrence simply re-resets the (still-empty) counters, which is
    // harmless since no data rows are consumed between them.
    if (WEHOLITE_TRIGGER.test(desc)) {
      if (Object.keys(occurrences).length > 0 || currentTank === 0) {
        currentTank += 1;
        occurrences = {};
      }
      continue;
    }
    if (currentTank === 0) continue;
    if (isStormBuildUpHeaderRow(desc)) continue;

    // Resolve which line item this row is by matching its label, honouring the
    // occurrence index for duplicated labels.
    const seen = occurrences[desc] ?? 0;
    const matches = WEHOLITE_ROW_DEFS.filter((d) => d.pattern.test(desc));
    if (matches.length === 0) continue;
    const rowDef = matches[Math.min(seen, matches.length - 1)];
    occurrences[desc] = seen + 1;
    const id = `wh_${currentTank}_${rowDef.suffix}`;

    // FP/OHP's real configured value can show up in either place depending on
    // the sheet — same "Add <pct>% FP/OHP on above" description convention as
    // Conc Attenuation's fp5/ohp15 (matched first, same regex/lookup as
    // parseConcAttenuationSheet above) when the description embeds it, with a
    // fallback to the row's own quantity cell for a sheet where the
    // description never gets past its "xxx" placeholder but the real % was
    // entered as the quantity instead.
    const configurableField = getWeholiteConfigurableField(id);
    let configSetFromDesc = false;
    if (configurableField) {
      const numMatch = desc.match(/(\d+(?:\.\d+)?)\s*%/);
      if (numMatch) {
        const val = parseFloat(numMatch[1]);
        if (val > 0) {
          weholiteConfigPct[id] = val;
          configSetFromDesc = true;
        }
      }
    }

    const rawQuant = row[quantCol];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const q = parseFloat(qStr);
    if (isNaN(q)) continue;

    let evaluateCode = '';
    if (evaluateCol >= 0) {
      const rawEval = String(row[evaluateCol] ?? '').trim();
      evaluateCode = rawEval.startsWith('#') ? '' : rawEval;
    }

    result[id] = { quant: q, evaluateCode };

    if (configurableField && !configSetFromDesc && q > 0) {
      weholiteConfigPct[id] = q;
    }
  }
  return { table: result, weholiteConfigPct };
};

// ─── Conc Attenuation build-up parsing ───────────────────────────────────────
//
// Same fix as parseWeholiteAttenuationSheet below: a positional row-index
// approach mis-maps every quantity whenever a row is missing/reordered.
// Instead each line item is matched by its description label. The sheet's own
// tank header is a fixed "CARLOW TANK" label repeated per block (not
// "Carlow Tank <n>" — the app's own on-screen supplierName/tank-number text is
// a display-only convenience, unrelated to the take-off template's fixed
// wording), so tanks are counted by trigger occurrence order, same convention
// as WEHOLITE_TRIGGER. Rows whose description embeds a supplierName also use
// the sheet's fixed literal "Carlow" rather than the app's configurable
// supplierName (state.concAttenuation.config?.supplierName), matching how
// WEHOLITE_ROW_DEFS below hardcodes "Weholite" regardless of that domain's
// own configurable supplierName.

const CONC_TANK_TRIGGER = /^CARLOW TANK$/i;

interface ConcRowDef {
  suffix: string;
  pattern: RegExp;
}

// digits-or-"xxx" alternation: matches either a real configured value or the
// unconfigured placeholder text, same convention as the mm patterns below.
const N = '(?:\\d+(?:\\.\\d+)?|xxx)';

const CONC_ROW_DEFS: ConcRowDef[] = [
  { suffix: 'supplyDesc',  pattern: /^Supply and installation of Carlow Tank\b/i },
  { suffix: 'excPit',      pattern: /^Excavate pit \(all-in\)$/i },
  { suffix: 'backfillWS',  pattern: /^Backfill working space with as dug material$/i },
  { suffix: 'backfillPit', pattern: /^Backfill pit with as dug material$/i },
  { suffix: 'levelComp',   pattern: /^Level & compact$/i },
  { suffix: 'mot150',      pattern: new RegExp(`^${N}mm thick MOT Type 1$`, 'i') },
  { suffix: 'conc75',      pattern: /^75mm concrete blinding$/i },
  { suffix: 'scabbling',   pattern: /^Scabbling to concrete surfaces$/i },
  { suffix: 'waterstop',   pattern: /^Hydrophillic waterstop$/i },
  { suffix: 'slab225',     pattern: /^225mm concrete slab$/i },
  { suffix: 'mesh',        pattern: /^Reinforcement mesh$/i },
  { suffix: 'supplyCar',   pattern: /^(?:Supply of Carlow materials|FPI Carlow Supply - [123])$/i },
  { suffix: 'install',     pattern: /^Carlow Installation$/i },
  { suffix: 'fp5',         pattern: new RegExp(`^Add ${N}% FP on above$`, 'i') },
  { suffix: 'ohp15',       pattern: new RegExp(`^Add ${N}% OHP on above$`, 'i') },
  { suffix: 'lpAttend',    pattern: /^MJ Evans L&P Attendance \(at 50% of Carlow programme period\)$/i },
  { suffix: 'access900',   pattern: /^900 x 675mm access opening$/i },
  { suffix: 'precastSlab', pattern: /^Precast slab with opening$/i },
  { suffix: 'brickwork',   pattern: /^Brickwork plus cover & frame to access chamber$/i },
  { suffix: 'liftEquip',   pattern: /^Allowance for specialist lifting equipment$/i },
  { suffix: 'scaffold',    pattern: /^Allowance for scaffolding \(possibly short sections req'd\)$/i },
  { suffix: 'haulRoad',    pattern: new RegExp(`^${N}m wide haul road \\(crane mat\\) in ${N}mm thk 6F2 to all sides$`, 'i') },
  { suffix: 'removeHaul',  pattern: /^Remove haul road, stockpile 6F2 for re-use onsite$/i },
  { suffix: 'crane',       pattern: new RegExp(`^Allowance for crane visits \\(${N} days as Carlow quote of ${N} day programme\\)$`, 'i') },
  { suffix: 'concPump',    pattern: new RegExp(`^Allowance for concrete pump visits \\(${N} days as Carlow quote\\)$`, 'i') },
  { suffix: 'leadEdge',    pattern: /^Allowance for leading edge protection$/i },
  { suffix: 'conc58',      pattern: new RegExp(`^Allowance for ${N}m³ concete requirement as Carlow quote$`, 'i') },
  { suffix: 'mortar1',     pattern: new RegExp(`^Allowance for ${N}m³ RTU mortar requirement as Carlow quote$`, 'i') },
  { suffix: 'steel',       pattern: new RegExp(`^Allowance for balance of ${N}t of steel reinforcement requirment as Carlow quote$`, 'i') },
  { suffix: 'formwork',    pattern: /^Allowance for formwork$/i },
  { suffix: 'skip',        pattern: new RegExp(`^Allowance for ${N} nr skip as Carlow quote$`, 'i') },
];

// Matches the conc_${n}_* ids flagged allowTextQuant in stormBuildUpConcAttenuation.ts.
const TEXT_QUANT_CONC_SUFFIXES = /_(conc75|scabbling|waterstop|slab225|mesh|access900|precastSlab|crane|concPump|conc58|mortar1|steel|skip)$/;

// Per-suffix pattern for pulling a configured value straight out of a row's
// own description cell — same "value embedded in description" convention as
// parsePondsSwalesSheet's topsoil mm, just with each row's own unit instead
// of always "mm". fp5/ohp15 are "%"; the rest are mm/days/m³/t/nr.
const CONC_NUMBER_PATTERNS: Record<string, RegExp> = {
  fp5: /(\d+(?:\.\d+)?)\s*%/,
  ohp15: /(\d+(?:\.\d+)?)\s*%/,
  mot150: /^(\d+(?:\.\d+)?)\s*mm/i,
  concPump: /\(\s*(\d+(?:\.\d+)?)\s*days/i,
  conc58: /(\d+(?:\.\d+)?)\s*m³/,
  mortar1: /(\d+(?:\.\d+)?)\s*m³/,
  steel: /(\d+(?:\.\d+)?)\s*t\b/i,
  skip: /(\d+(?:\.\d+)?)\s*nr\b/i,
};

const parseConcAttenuationSheet = (
  rows: unknown[][],
  descCol: number,
  quantCol: number,
  evaluateCol: number,
): { table: Record<string, FoulBuildUpRowData>; concConfigPct: Record<string, number>; drawingRefs: Record<string, string> } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const concConfigPct: Record<string, number> = {};
  const drawingRefs: Record<string, string> = {};
  let currentTank = 0;

  for (const row of rows) {
    const desc = String(row[descCol] ?? '').trim();
    if (!desc) continue;

    if (CONC_TANK_TRIGGER.test(desc)) {
      currentTank += 1;
      continue;
    }
    if (currentTank === 0) continue;
    if (isStormBuildUpHeaderRow(desc)) continue;

    const rowDef = CONC_ROW_DEFS.find((d) => d.pattern.test(desc));
    if (!rowDef) continue;
    const id = `conc_${currentTank}_${rowDef.suffix}`;

    // FP/OHP/MOT Type 1/etc.'s static description carries an "xxx" placeholder
    // value — get the real value straight off the sheet's own description
    // cell, same convention as parsePondsSwalesSheet's topsoil mm.
    if (rowDef.suffix === 'supplyCar') {
      const optIdx = CONC_SUPPLY_OPTIONS.findIndex((o) => o.n.toLowerCase() === desc.toLowerCase());
      if (optIdx >= 0) concConfigPct[id] = optIdx + 1;
    }
    const configurableField = getConcConfigurableField(id);
    let configSetFromDesc = false;
    if (configurableField) {
      const pattern = CONC_NUMBER_PATTERNS[configurableField.suffix];
      const numMatch = pattern ? desc.match(pattern) : null;
      if (numMatch) {
        const val = parseFloat(numMatch[1]);
        if (val > 0) {
          concConfigPct[id] = val;
          configSetFromDesc = true;
        }
      }
    }

    // haulRoad/crane each embed two independent numbers in one description —
    // read both into their own dedicated config keys (see makeConcTankRows).
    if (id.endsWith('_haulRoad')) {
      const widthMatch = desc.match(/^(\d+(?:\.\d+)?)\s*m\s+wide/i);
      if (widthMatch) {
        const v = parseFloat(widthMatch[1]);
        if (v > 0) concConfigPct[id.replace(/_haulRoad$/, '_haulRoadWidth')] = v;
      }
      const thickMatch = desc.match(/in\s*(\d+(?:\.\d+)?)\s*mm\s*thk/i);
      if (thickMatch) {
        const v = parseFloat(thickMatch[1]);
        if (v > 0) concConfigPct[id.replace(/_haulRoad$/, '_haulRoadThick')] = v;
      }
    }
    if (id.endsWith('_crane')) {
      const visitMatch = desc.match(/\(\s*(\d+(?:\.\d+)?)\s*days/i);
      if (visitMatch) {
        const v = parseFloat(visitMatch[1]);
        if (v > 0) concConfigPct[id.replace(/_crane$/, '_craneVisit')] = v;
      }
      const programmeMatch = desc.match(/of\s*(\d+(?:\.\d+)?)\s*day\s*programme/i);
      if (programmeMatch) {
        const v = parseFloat(programmeMatch[1]);
        if (v > 0) concConfigPct[id.replace(/_crane$/, '_craneProgramme')] = v;
      }
    }

    // supplyDesc embeds three tank dimensions plus the drawing reference (see makeConcTankRows).
    if (id.endsWith('_supplyDesc')) {
      const dimsMatch = desc.match(/-\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m\s*deep/i);
      if (dimsMatch) {
        const [, w, l, d] = dimsMatch;
        if (parseFloat(w) > 0) concConfigPct[id.replace(/_supplyDesc$/, '_tankWidth')] = parseFloat(w);
        if (parseFloat(l) > 0) concConfigPct[id.replace(/_supplyDesc$/, '_tankLength')] = parseFloat(l);
        if (parseFloat(d) > 0) concConfigPct[id.replace(/_supplyDesc$/, '_tankDepth')] = parseFloat(d);
      }
      const refMatch = desc.match(/drawings\s+(\S+)\s*$/i);
      if (refMatch && refMatch[1] !== CONC_DEFAULT_DRAWING_REF) drawingRefs[id.replace(/_supplyDesc$/, '_drawingRef')] = refMatch[1];
    }

    const rawQuant = row[quantCol];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const q = parseFloat(qStr);

    let evaluateCode = '';
    if (evaluateCol >= 0) {
      const rawEval = String(row[evaluateCol] ?? '').trim();
      evaluateCode = rawEval.startsWith('#') ? '' : rawEval;
    }

    // A real client take-off commonly types the configured value straight
    // into the Qty cell instead of embedding it in the (often still-"xxx")
    // description — same "either convention" fallback as
    // parseWeholiteAttenuationSheet's fp/ohp handling above. mot150 is
    // excluded: its Qty column is the MOT slab's m² area, unrelated to its
    // mm-thickness config value.
    if (configurableField && !configSetFromDesc && configurableField.suffix !== 'mot150' && !isNaN(q) && q > 0) {
      concConfigPct[id] = q;
    }

    if (TEXT_QUANT_CONC_SUFFIXES.test(id)) {
      // These rows' Qty column is free text (see allowTextQuant on their
      // StormBuildUpRowDef) — the sheet may hold either a spec note (e.g.
      // "by Carlow") or a plain number a real client take-off typed straight
      // into the cell instead of embedding it in the description (e.g. "58"
      // rather than "...as Carlow quote"). Both StormBuildUpTable.tsx and
      // stormSummaryUtils.ts read only `quantText` for these rows, never
      // `quant` — so a numeric cell must still populate `quantText`, or it
      // parses fine here but renders blank everywhere downstream.
      result[id] = { quant: isNaN(q) ? null : q, evaluateCode, quantText: qStr };
      continue;
    }

    if (isNaN(q)) continue;

    result[id] = { quant: q, evaluateCode };
  }
  return { table: result, concConfigPct, drawingRefs };
};

// ─── Box Culverts build-up parsing ───────────────────────────────────────────

const CULVERT_TRIGGERS = Array.from({ length: MAX_BOX_CULVERT_SECTIONS }, (_, i) => ({
  culvert: i + 1,
  pattern: new RegExp(`^Culvert\\s*${i + 1}$`, 'i'),
}));

const CULVERT_ROW_DEFS = [
  { suffix: 'excavate',     pattern: /^Excavate$/i },
  { suffix: 'earthwork',    pattern: /^Earthwork\s+support$/i },
  { suffix: 'compact',      pattern: /^Compact\s+Base\s+of\s+Trench$/i },
  { suffix: 'deposit',      pattern: /^Deposit\s+on\s+site$/i },
  // mm suffix is optional/variable — "Concrete blinding" alone (legacy) or
  // "Concrete blinding 50mm thick" (current, configured) both match; the
  // actual number (if present) is read separately below into culvertConfigMm.
  { suffix: 'concBlinding', pattern: /^Concrete\s+blinding(\s+\d+(\.\d+)?\s*mm\s+thick)?$/i },
  { suffix: 'bedTop',       pattern: /^Pipe\s+bedding(\s+\d+(\.\d+)?\s*mm)?\s+thick\s+top\s+&\s+bottom$/i },
  { suffix: 'bedSide',      pattern: /^Pipe\s+bedding(\s+\d+(\.\d+)?\s*mm)?\s+thick\s+sides$/i },
  { suffix: 'backfill',     pattern: /^Backfill/i },
  { suffix: 'purchase',     pattern: /^Purchase\s+price\s+of\s+culvert/i },
  { suffix: 'labour',       pattern: /^Labour\s+&\s+Plant\s+-\s+Install\s+culverts$/i },
];

// The sheet's own "Height" row has the label one column to the LEFT of the
// usual description column — "Height" sits in col B (the Item column, same
// one every other row leaves blank), and its value ("1.20", metres) is the
// one that's actually in col C (descCol), where every other row's text
// description normally lives. Confirmed against the actual workbook.
const CULVERT_HEIGHT_LABEL_PATTERN = /^height$/i;

// The specHdr sub-header itself — "<size>mm Box Culvert - <depth>m dp" — both
// numbers embedded straight in the one description cell, same convention as
// Ponds' topsoil mm. Deliberately separate from the Height row above (the
// two aren't the same figure, confirmed against the actual workbook).
const CULVERT_SPEC_HDR_PATTERN = /^(\d+(?:\.\d+)?)\s*mm\s*Box\s*Culvert\s*-\s*(\d+(?:\.\d+)?)\s*m\s*dp$/i;

const parseBoxCulvertsSheet = (
  rows: unknown[][],
  descCol: number,
  quantCol: number,
  evaluateCol: number,
): { table: Record<string, FoulBuildUpRowData>; culvertConfigMm: Record<string, number>; culvertBackfillType: Record<string, string> } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const culvertConfigMm: Record<string, number> = {};
  const culvertBackfillType: Record<string, string> = {};
  let currentCulvert = -1;
  const triggeredCulverts = new Set<number>();

  for (const row of rows) {
    const desc = String(row[descCol] ?? '').trim();
    const itemLabel = String(row[descCol - 1] ?? '').trim();

    if (currentCulvert !== -1 && CULVERT_HEIGHT_LABEL_PATTERN.test(itemLabel)) {
      const heightM = parseFloat(desc);
      if (!isNaN(heightM) && heightM > 0) {
        culvertConfigMm[`bc_${currentCulvert}_height`] = Math.round(heightM * 1000);
      }
      continue;
    }

    if (currentCulvert !== -1) {
      const specMatch = desc.match(CULVERT_SPEC_HDR_PATTERN);
      if (specMatch) {
        const sizeMm = Math.round(parseFloat(specMatch[1]));
        const depthM = parseFloat(specMatch[2]);
        if (sizeMm > 0) culvertConfigMm[`bc_${currentCulvert}_specSize`] = sizeMm;
        if (!isNaN(depthM) && depthM > 0) culvertConfigMm[`bc_${currentCulvert}_specDepth`] = Math.round(depthM * 1000);
        continue;
      }
    }

    if (!desc) continue;

    const trigger = CULVERT_TRIGGERS.find(
      (t) => t.pattern.test(desc) && !triggeredCulverts.has(t.culvert)
    );
    if (trigger) {
      triggeredCulverts.add(trigger.culvert);
      currentCulvert = trigger.culvert;
      continue;
    }

    if (currentCulvert === -1) continue;

    const rowDef = CULVERT_ROW_DEFS.find((d) => d.pattern.test(desc));
    if (!rowDef) continue;

    // Rows whose static description carries an "xxx" placeholder thickness
    // (Concrete Blinding/Pipe Bedding Top & Bottom/Pipe Bedding Sides) get
    // their real mm value read straight off the sheet's own description
    // cell — same convention as parsePondsSwalesSheet's topsoil mm. FP is a
    // percentage rather than a thickness (and can be a decimal, e.g. 4.5%),
    // so it's read as "<n>%" instead of "<n>mm".
    const configurableField = getBoxCulvertConfigurableField(`bc_${currentCulvert}_${rowDef.suffix}`);
    if (configurableField) {
      const mmMatch = desc.match(/(\d+(?:\.\d+)?)\s*mm/i);
      if (mmMatch) {
        const mm = Math.round(parseFloat(mmMatch[1]));
        if (mm > 0) culvertConfigMm[`bc_${currentCulvert}_${rowDef.suffix}`] = mm;
      } else {
        const pctMatch = desc.match(/(\d+(?:\.\d+)?)\s*%/);
        if (pctMatch) {
          const pct = parseFloat(pctMatch[1]);
          if (pct > 0) culvertConfigMm[`bc_${currentCulvert}_${rowDef.suffix}`] = pct;
        }
      }
    }

    // Backfill Type dropdown — read straight off the sheet's own "Backfill
    // (<type> Under Carriageway)" description cell, same convention as the
    // mm fields above (confirmed against the actual workbook text).
    if (rowDef.suffix === 'backfill') {
      const typeMatch = desc.match(/^Backfill\s*\(([^)]+?)\s*Under Carriageway\)/i);
      if (typeMatch) {
        culvertBackfillType[`bc_${currentCulvert}_backfill`] = typeMatch[1].trim();
      }
    }

    const rawQuant = row[quantCol];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const q = parseFloat(qStr);
    if (isNaN(q)) continue;

    let evaluateCode = '';
    if (evaluateCol >= 0) {
      const rawEval = String(row[evaluateCol] ?? '').trim();
      evaluateCode = rawEval.startsWith('#') ? '' : rawEval;
    }

    result[`bc_${currentCulvert}_${rowDef.suffix}`] = { quant: q, evaluateCode };
  }
  return { table: result, culvertConfigMm, culvertBackfillType };
};

// Column definitions for manholes
const manholeColumns: ExcelColumn[] = [
  { key: "ref", title: "MH Ref" },
  {
    key: "coverLevel",
    title: "Cover Level (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "invertLevel",
    title: "Invert Level (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depth",
    title: "Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthCategory",
    title: "Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "diameter", title: "Diameter" },
  { key: "type", title: "Type" },
];

// Column definitions for storm sewer pipe runs
const pipeRunColumns: ExcelColumn[] = [
  { key: "fromMH", title: "From MH" },
  { key: "toMH", title: "To MH" },
  {
    key: "length",
    title: "Length (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "diameter", title: "Pipe Diameter" },
  { key: "pipeType", title: "Pipe Type" },
  { key: "backfillType", title: "Backfill Type" },
  {
    key: "depthFrom",
    title: "Depth From (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthTo",
    title: "Depth To (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "averageDepth",
    title: "Avg Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthCategory",
    title: "MH Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "runDepthCategory",
    title: "Run Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "asDugBackfill",
    title: "As Dug Backfill",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "rockers", title: "Rockers", render: (value) => Number(value || 0) },
  {
    key: "junctions",
    title: "Junctions",
    render: (value) => Number(value || 0),
  },
];

// Column definitions for lateral connections
const lateralColumns: ExcelColumn[] = [
  { key: "toManhole", title: "Ref" },
  {
    key: "length",
    title: "Length (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "pipeDiameter", title: "Pipe Diameter" },
  { key: "manholeDiameter", title: "Manhole Diameter" },
  {
    key: "backdrop",
    title: "Backdrop",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depth",
    title: "Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "requiresFirstOff", title: "Requires First Off" },
  { key: "firstOffType", title: "First Off Type" },
  {
    key: "lateralManholeDepthCategory",
    title: "Lateral MH Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthOffMainRun",
    title: "Depth Off Main Run",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "averageDepth",
    title: "Average Depth",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "avgDepthCategory",
    title: "Avg Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "rockers", title: "Rockers", render: (value) => Number(value || 0) },
];

// Column definitions for gully connections
const gullyColumns: ExcelColumn[] = [
  { key: "gullyRef", title: "Gully Ref" },
  { key: "location", title: "Location" },
  { key: "gullyType", title: "Gully Type" },
  { key: "toPipeRun", title: "To Pipe Run" },
  {
    key: "length",
    title: "Length (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "pipeDiameter", title: "Pipe Diameter" },
  {
    key: "gullyDepth",
    title: "Gully Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthOffMainRun",
    title: "Depth Off Main Run",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "averageDepth",
    title: "Average Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "avgDepthCategory",
    title: "Avg Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "gulleyIntoManhole",
    title: "Gulley into Manhole",
    render: (value) => Number(value || 0),
  },
  {
    key: "junction150x150",
    title: "150×150 Junction",
    render: (value) => Number(value || 0),
  },
  { key: "gulleys", title: "Gulley(s)", render: (value) => Number(value || 0) },
  {
    key: "connectionDepth",
    title: "Connection Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthCategory",
    title: "Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
];

/**
 * Export storm sewers data (manholes, pipe runs, laterals, gullies) to Excel
 */
export const exportStormSewersToExcel = async (
  data: StormSewersExcelData,
  projectName: string = "Storm Sewers"
): Promise<void> => {
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `${projectName}_StormSewers_${timestamp}`;

  // Transform gullies to show pipe run reference instead of ID
  const transformedGullies = (data.gullies || []).map((gully) => {
    const pipeRun = (data.pipeRuns || []).find(
      (pr) => pr.id === gully.toPipeRun
    );
    return {
      ...gully,
      toPipeRun: pipeRun
        ? `${pipeRun.fromMH}-${pipeRun.toMH}`
        : gully.toPipeRun || "",
    };
  });

  const sheets = [
    {
      name: "Manholes",
      columns: manholeColumns,
      data: data.manholes || [],
    },
    {
      name: "Pipe Runs",
      columns: pipeRunColumns,
      data: data.pipeRuns || [],
    },
    {
      name: "Laterals",
      columns: lateralColumns,
      data: data.laterals || [],
    },
    {
      name: "Gullies",
      columns: gullyColumns,
      data: transformedGullies,
    },
  ];

  // Filter out empty sheets
  const nonEmptySheets = sheets.filter((sheet) => sheet.data.length > 0);

  if (nonEmptySheets.length === 0) {
    throw new Error("No data to export");
  }

  await exportToExcelMultiSheet({
    filename,
    includeBranding: true,
    projectName: projectName,
    sheets: nonEmptySheets,
  });
};

/**
 * Generate Excel template for storm sewers import
 */
export const generateStormSewersTemplate = async (
  projectName: string = "StormSewers"
): Promise<void> => {
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `${projectName}_StormSewers_Template_${timestamp}`;

  const sheets = [
    { name: "Manholes", columns: manholeColumns },
    { name: "Pipe Runs", columns: pipeRunColumns },
    { name: "Laterals", columns: lateralColumns },
    { name: "Gullies", columns: gullyColumns },
  ];

  await generateMultiSheetTemplate(filename, sheets);
};

// Helper function to ensure diameter values have 'mm' unit (used by export functions)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ensureDiameterUnit = (value: any): string => {
  if (!value) return "150mm"; // Default value
  const strValue = String(value).trim();

  // If it's just a number (like "150"), add "mm"
  if (/^\d+$/.test(strValue)) {
    return `${strValue}mm`;
  }

  // If it already has a unit or is non-numeric, return as is
  return strValue;
};

// ─── Helpers for direct XLSX import ──────────────────────────────────────────

/** Convert raw rows (array-of-arrays) + headers into objects keyed by header. */
const rowsToObjects = (
  raw: unknown[][],
  headers: string[]
): Record<string, unknown>[] =>
  raw
    .filter((r) => r.some((c) => c !== null && c !== undefined && c !== ''))
    .map((r) =>
      Object.fromEntries(
        headers.map((h, i) => [h.trim(), r[i] ?? null])
      )
    );

/** Pick first non-null / non-empty value from a list of possible column names. */
const col = (row: Record<string, unknown>, ...names: string[]): unknown => {
  for (const name of names) {
    const v = row[name];
    if (v !== null && v !== undefined && String(v).trim() !== '') return v;
  }
  return null;
};

const toNum = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return isNaN(n) ? fallback : n;
};

const toStr = (v: unknown, fallback = ''): string =>
  v !== null && v !== undefined ? String(v).trim() : fallback;

const ensureDiameterUnitWithDefault = (v: unknown, def = '1200mm'): string => {
  if (!v) return def;
  const s = String(v).trim();
  return /^\d+$/.test(s) ? `${s}mm` : s;
};

const isNaOrEmpty = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === '' || s === '#N/A' || s.toLowerCase() === 'n/a';
};

// Manhole references: Excel formula returns a numeric zero when the cell is
// blank — not always the exact string "0" (e.g. "0.0", "0.00", "-0" depending
// on the source formula/number format), so check numerically rather than
// with an exact string match. Mirrors Foul Sewers' isEmptyManholeRef
// (foulSewersExcelUtils.ts).
const isEmptyManholeRef = (v: unknown): boolean => {
  if (isNaOrEmpty(v)) return true;
  const n = Number(String(v).trim());
  return !isNaN(n) && n === 0;
};

// Excel's "Junction size" column is free text — some templates store it as
// the plain "{main} x {branch}" pair without the "mm junction" suffix that
// calcStormPipeRunDerived produces for manually-entered rows (matching Foul
// Sewers' calcFoulJunctionSize format). Saddle/Core Drill values are already
// complete and left untouched. If the "{main} x {branch}" pair is missing or
// zero on either side (e.g. "150 x", "150 xmm junction", "0 x 0" — a
// formula-driven cell whose source diameters weren't both filled in), blank
// it out instead of appending the suffix to an incomplete/bogus pair —
// matching calcStormPipeRunDerived, which likewise only builds the text when
// both diameters are present.
const normalizeJunctionSizeText = (v: unknown): string => {
  const s = toStr(v);
  if (!s) return s;
  const lower = s.toLowerCase();
  if (lower.includes('saddle') || lower.includes('core drill')) return s;
  const pair = s.match(/(\d*)\s*x\s*(\d*)/i);
  if (pair && !((parseInt(pair[1], 10) || 0) > 0 && (parseInt(pair[2], 10) || 0) > 0)) return '';
  if (lower.endsWith('junction')) return s;
  return lower.endsWith('mm') ? `${s} junction` : `${s}mm junction`;
};

const normalizePipeType = (v: unknown): string => {
  const s = String(v ?? '').toLowerCase().trim();
  if (s.includes('ultra')) return 'ultra';
  if (s.includes('rigi')) return 'rigi';
  if (s.includes('conc')) return 'concrete';
  return 'clay';
};

const resolveDropdown = (v: unknown): 'N/A' | 'Stainless' | 'Galvanised' | undefined => {
  const s = String(v ?? '').trim();
  if (s === 'N/A' || s === 'n/a') return 'N/A';
  if (s.toLowerCase().startsWith('stain')) return 'Stainless';
  if (s.toLowerCase().startsWith('galv')) return 'Galvanised';
  return undefined;
};

const resolveEasiTrad = (v: unknown): 'easi' | 'trad' | undefined => {
  const s = String(v ?? '').toLowerCase().trim();
  if (s.startsWith('e')) return 'easi';
  if (s.startsWith('t')) return 'trad';
  return undefined;
};

// ─── Transformer: 'Storm Manhole Schedule' ───────────────────────────────────

const transformStormManholeSchedule = (
  rows: Record<string, unknown>[]
): Manhole[] =>
  rows
    .filter((r) => col(r, 'Manhole') !== null || col(r, 'Type') !== null)
    .map((r, i) => {
      const coverLevel  = toNum(col(r, 'Cover'));
      const invertLevel = toNum(col(r, 'Invert'));

      return {
        id: `imp_mh_${i}`,
        ref:          toStr(col(r, 'Manhole'), `MH${i + 1}`),
        type:         toStr(col(r, 'Type')),
        easiTrad:     resolveEasiTrad(col(r, 'Easi / Trad')),
        diameter:     ensureDiameterUnitWithDefault(col(r, 'Diam'), '1200mm'),
        coverLevel,
        invertLevel,
        sump:          toNum(col(r, 'Sump')),
        depth:         toNum(col(r, 'Depth To', 'Depth (m)')),
        depthCategory: toNum(col(r, 'Depth Category', 'Depth')),
        ladders:      toNum(col(r, 'Ladders if not incl in chamber price', 'Ladders (m)')),
        safetyChains: toNum(col(r, 'Safety Chains')),
        backdrop:     toNum(col(r, 'Backdrop')),
        muckStopper:  toNum(col(r, 'Muck stopper')),
        amps:         toNum(col(r, 'AMPS')),
      };
    });

// ─── Transformer: 'Storm Take off' — Storm Sewer Runs ────────────────────────

// A row with `_hasMain: false` (set by the caller's row-splitting loop) has no
// Main Sewer Run on this physical Excel row — it exists purely to keep index
// alignment with a House Lateral / Gully Lateral on the same row (pipeRuns[i]
// is matched positionally against both laterals[i] and gullies[i] throughout
// StormSewersTakeoff.tsx, e.g. getStormJunctionQty, calcStormLateralDerived,
// calcStormGullyDerived). Identity/classification fields (ref, MH refs,
// diameter, pipeType, backfillType, junctionSize) are blanked instead of
// falling through to a non-blank default, which would otherwise seed fake
// values that can falsely match a real run's branch-diameter comparisons in
// Storm Summary junction calcs. Depth/quantity columns are still read as-is:
// the R&S sheet's derived-depth formulas (Avg Depth Category, etc.) commonly
// stay populated even when the MH ref cells on that row are blank.
const transformStormSewerRuns = (rows: Record<string, unknown>[]): StormSewerRun[] =>
  rows.map((r, i) => {
    const hasMain = Boolean(r['_hasMain']);
    return {
      id:                 `imp_run_${i}`,
      ref:                hasMain ? toStr(col(r, 'Run ref')) : '',
      fromMH:             hasMain ? toStr(col(r, 'Manhole From')) : '',
      toMH:               hasMain ? toStr(col(r, 'Manhole To')) : '',
      length:             toNum(col(r, 'Run Length (m)', 'Run Length')),
      // No fallback default even when hasMain — a real run with a blank Pipe Ø
      // cell should stay blank, not silently become "150mm" (same fix already
      // applied to House Laterals' pipeDiameter below, and to Foul's
      // equivalent Main Sewer Run diameter).
      diameter:           hasMain ? ensureDiameterUnitWithDefault(col(r, 'Pipe Ø (mm)', 'Pipe Ø', 'Pipe O (mm)'), '') : '',
      pipeType:           hasMain ? normalizePipeType(col(r, 'Clay, Conc, Ultra, Rigi', 'Clay/Conc/Ultra/Rigi')) : '',
      backfillType:       hasMain ? normalizeBackfillType(col(r, 'Backfill')) : undefined,
      roadConstDepth:     toNum(col(r, 'Road Const Depth (0.45)', 'Road Const Depth'), hasMain ? 0.45 : 0),
      depthFrom:          toNum(col(r, 'Depth From (m)', 'Depth From')),
      depthTo:            toNum(col(r, 'Depth To (m)', 'Depth To')),
      averageDepth:       toNum(col(r, 'Avg Depth (m)', 'Avg Depth')),
      depthCategory:      0,
      runDepthCategory:   toNum(col(r, 'Avg Run depth Category', 'Avg Depth Category', 'Run Depth Category')),
      rockers:            toNum(col(r, 'Rockers (nr)', 'Rockers')),
      concreteProtection: toNum(col(r, 'Concrete Protection (m, if App)', 'Concrete Protection')),
      junctionSize:       hasMain ? normalizeJunctionSizeText(col(r, 'Junction size', 'Junction Size')) : '',
      junctions:          toNum(col(r, 'Junctions (Nr)', 'Junctions')),
    };
  });

// ─── Transformer: 'Storm Take off' — House Laterals ─────────────────────────

// `_hasLateral: false` means this row has no real lateral connection (no
// Manhole Ref) — every field is blanked/zeroed rather than importing any
// data for it, even the demarc/avg-depth columns that derive from the paired
// Main Sewer Run's depth category via an Excel formula and would otherwise
// stay populated regardless of whether this row has a lateral connection.
const transformStormLaterals = (rows: Record<string, unknown>[]): LateralConnection[] =>
  rows.map((r, i) => {
    const hasLateral = Boolean(r['_hasLateral']);
    if (!hasLateral) {
      return {
        id:                  `imp_lat_${i}`,
        ref:                 '',
        connectionType:      'direct_to_manhole' as const,
        toManhole:           '',
        junctionOffRun:      0,
        pipeType:            '',
        pipeDiameter:        '',
        length:              0,
        demarcCoverLevel:    0,
        demarcInvertLevel:   0,
        demarcDepth:         0,
        demarcDepthCategory: 0,
        averageDepth:        0,
        avgDepthCategory:    0,
        nme:                 0,
        count1200:           0,
        backfillType:        undefined,
        manholeDiameter:     '',
        backdrop:            0,
        depth:               0,
        requiresFirstOff:    false,
        rockers:             0,
      };
    }
    return {
      id:                  `imp_lat_${i}`,
      ref:                 `L${i + 1}`,
      connectionType:      'direct_to_manhole' as const,
      toManhole:           toStr(col(r, 'Manhole Ref (mm)', 'Manhole Ref')),
      junctionOffRun:      toNum(col(r, 'Junction off run (Nr)', 'Junction off run')),
      pipeType:            normalizePipeType(col(r, 'Clay, Conc, Ultra, Rigi', 'Clay/Conc/Ultra/Rigi')),
      pipeDiameter:        ensureDiameterUnitWithDefault(col(r, 'Pipe Ø (mm)', 'Pipe Ø', 'Pipe O (mm)'), ''),
      length:              toNum(col(r, 'Length (m)', 'Length')),
      demarcCoverLevel:    toNum(col(r, 'Demarc Cover (m)', 'Demarc Cover')),
      demarcInvertLevel:   toNum(col(r, 'Demarc Invert (m)', 'Demarc Invert')),
      demarcDepth:         toNum(col(r, 'Demarc Depth (m)', 'Demarc Depth')),
      demarcDepthCategory: toNum(col(r, 'Depth Category')),
      averageDepth:        toNum(col(r, 'Ave Run Depth (m)', 'Ave Run Depth')),
      avgDepthCategory:    toNum(col(r, 'Avg Run depth Category', 'Avg Run Category (m)', 'Avg Run Category', 'Avg Depth Category')),
      nme:                 toNum(col(r, 'NME')),
      count1200:           toNum(col(r, '1200mmØ chamber', '1200 (Nr)', '1200')),
      backfillType:        normalizeBackfillType(col(r, '_rowBackfill', 'Backfill')),
      manholeDiameter:     '1200mm',
      backdrop:            0,
      depth:               0.6,
      requiresFirstOff:    false,
      rockers:             2,
    };
  });

// ─── Transformer: 'Storm Take off' — Gully Laterals ─────────────────────────

const isPlaceholderRunRef = (v: unknown): boolean => {
  if (isNaOrEmpty(v)) return true;
  const s = String(v).trim();
  // Filter template placeholder values like "0-0", "0-", "-0", "0", "0-00", etc.
  return /^0[-\s]*0*$|^[-\s]*0*[-\s]*$/.test(s) || s === '0-0' || s.startsWith('0-') || s === '0';
};

// `_hasGully: false` means this row has no real gully connection (no Run
// Ref) — every field is blanked/zeroed rather than importing any data for
// it, matching the same rule applied to House Laterals above (gated there on
// Manhole Ref instead).
const transformStormGullies = (rows: Record<string, unknown>[]): GullyConnection[] =>
  rows.map((r, i) => {
    const hasGully = Boolean(r['_hasGully']);
    if (!hasGully) {
      return {
        id:                 `imp_gully_${i}`,
        gullyRef:           '',
        location:           '',
        gullyType:          'road' as const,
        connectionType:     'junction_on_run' as const,
        toPipeRun:          '',
        length:             0,
        pipeDiameter:       '',
        gullyDepth:         0.9,
        connectionDepth:    0,
        depthCategory:      0,
        depthOffMainRun:    0,
        averageDepth:       0,
        avgDepthCategory:   0,
        junction150x150:    0,
        gulleyIntoManhole:  0,
        gulleys:            0,
        runRef:             '',
        pipeType:           '',
        concBS:             0,
      };
    }
    const runRef = toStr(col(r, 'Run Ref'));
    const pipeDiameterCell = col(r, 'Pipe Ø');
    const pipeTypeCell     = col(r, 'Clay, Conc, Ultra, Rigi');
    return {
      id:                 `imp_gully_${i}`,
      gullyRef:           runRef,
      location:           '',
      gullyType:          'road' as const,
      connectionType:     'junction_on_run' as const,
      toPipeRun:          runRef,
      length:             toNum(col(r, 'Length')),
      // No fallback default here (unlike other pipeDiameter/pipeType uses of
      // these helpers elsewhere in this file) — a real gully row with a blank
      // Pipe Ø / material cell should stay blank, not silently become
      // "150mm"/"clay".
      pipeDiameter:       !isNaOrEmpty(pipeDiameterCell) ? ensureDiameterUnitWithDefault(pipeDiameterCell) : '',
      // Gully Depth is a fixed constant (0.900m), not user-editable and not
      // read from the sheet — matches calcStormGullyDerived in StormSewersTakeoff.tsx.
      gullyDepth:         0.9,
      connectionDepth:    0,
      depthCategory:      0,
      depthOffMainRun:    toNum(col(r, 'Avg Run Depth')),
      averageDepth:       toNum(col(r, 'Ave Run Depth')),
      avgDepthCategory:   toNum(col(r, 'Avg Run depth Category', 'Avg Depth Category')),
      junction150x150:    toNum(col(r, 'Saddle Junction')),
      gulleyIntoManhole:  toNum(col(r, '150mmØ Rocker (conn into MH)')),
      gulleys:            toNum(col(r, 'Gullies')),
      runRef,
      pipeType:           !isNaOrEmpty(pipeTypeCell) ? normalizePipeType(pipeTypeCell) : '',
      concBS:             toNum(col(r, 'Conc B&S')),
    };
  });

// ─── Shared sheet reader ──────────────────────────────────────────────────────

const readSheet = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  XLSX: any,
  ws: object,
  headerRowOffset: number,
  dataStartOffset: number
): { rows: Record<string, unknown>[] } => {
  const raw: unknown[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1, defval: null, raw: false, blankrows: false, range: headerRowOffset,
  });
  if (!raw || raw.length === 0 || !raw[0]) return { rows: [] };
  const headers = (raw[0] as unknown[]).map((h) => String(h ?? '').trim());
  const rows    = rowsToObjects(raw.slice(dataStartOffset) as unknown[][], headers);
  return { rows };
};

// ─── 'Storm Summary' sheet — manual EO/Redundant Sewer quantities ──────────
//
// Same rationale as Foul Sewers' parseFoulSummarySheet (foulSewersExcelUtils.ts):
// this app's own Storm Summary tab calculates most rows (Drainage Runs,
// Manholes, Junctions, Lateral Connections, Gully Laterals) straight from
// pipeRuns/manholes/laterals/gullies — only EO Concrete Bed & Surround, EO
// Items, Removal of Redundant Sewer, and the Manholes section's Clegg
// testing row are freely-typed manual inputs with no formula backing them
// (stormSummaryUtils.ts's STORM_EO_CONC_BED_ITEMS/STORM_EO_ITEMS/
// STORM_REDUNDANT_SEWER_ITEMS, plus the standalone 'smh-clegg' row, read via
// buildStormSummaryData's `getManual`). Match each sheet row's description
// text (checked across every column except that row's own quantity column)
// against these known descriptions; only a matched row's quantity value is
// applied, everything else is left untouched. Unlike Foul, EO Concrete Bed
// & Surround keys off each item's resource code (`seo-cb-${code}`), not its
// array index — buildStormSummaryData does the same, since this list runs
// 150mm–2400mmØ (18 items) and isn't guaranteed to stay in a fixed order.
const STORM_SUMMARY_QUANT_COL = 8; // column I — default for most sections

interface StormSummaryManualRow {
  key: string;
  desc: string;
  /** 0-based column index for this row's quantity, if it differs from column I. */
  quantCol?: number;
  /** When true, a negative quantity is imported as-is instead of being dropped. */
  allowNegative?: boolean;
}

const STORM_SUMMARY_MANUAL_ROWS: StormSummaryManualRow[] = [
  ...STORM_EO_CONC_BED_ITEMS.map((item) => ({ key: `seo-cb-${item.code}`, desc: item.desc })),
  ...STORM_EO_ITEMS.map((item, idx) => ({ key: `seo-misc-${idx}`, desc: item.desc })),
  ...STORM_REDUNDANT_SEWER_ITEMS.map((item, idx) => ({ key: `seo-redundant-${idx}`, desc: item.desc })),
  // Manholes section — same layout as Foul's 'mh-clegg', whose quantity
  // sits in column R (idx 17), not column I like the rest. The Hydrobrake/
  // Penstock/Weir wall items and the Filter Drain section immediately
  // following it in the sheet share the same column R placement.
  { key: 'smh-clegg', desc: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)", quantCol: 17 },
  ...STORM_HYDROBRAKE_PENSTOCK_WEIR_ITEMS.map((item, idx) => ({ key: `smh-hw-${idx}`, desc: item.desc, quantCol: 17 })),
  ...STORM_FILTER_DRAIN_ITEMS.map((item, idx) => ({ key: `sfilterdrain-${idx}`, desc: item.desc, quantCol: 17 })),
  // House Laterals section — column AB (idx 27), not column R like the
  // Manholes-area rows above; the House Laterals sheet block uses a wider
  // layout than Manholes.
  { key: 'slat-b125-cover', desc: 'EO B125 Cover & frame to drives', quantCol: 27 },
  // "ADJUSTMENT FOR ROAD CONSTRUCTION GREATER THAN 450mm" → Credits — manual,
  // negative quantities entered against each of the 4 named items, same
  // column R placement as the rest of the Manholes section.
  { key: 'smh-adj-trench', desc: 'Trench Excavation', quantCol: 17, allowNegative: true },
  { key: 'smh-adj-ews', desc: 'EWS', quantCol: 17, allowNegative: true },
  { key: 'smh-adj-disposal', desc: 'Disposal', quantCol: 17, allowNegative: true },
  { key: 'smh-adj-backfill', desc: 'Backfill', quantCol: 17, allowNegative: true },
];

// Confirmed against a real client 'Foul Summary' workbook that AMHEO006 is
// misspelled "offite" there (missing the "s") — Storm's sheet is generated
// from the same master template, so pre-empting the same misspelling here
// rather than waiting to rediscover it row-by-row.
const STORM_SUMMARY_DESC_ALIASES: StormSummaryManualRow[] = [
  { key: 'seo-misc-0', desc: 'EO for working in offite carriageway' },
];

const normalizeStormSummaryDesc = (v: unknown): string =>
  String(v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const parseStormSummarySheet = (rows: unknown[][]): Record<string, number> => {
  const descLookup = new Map(
    [...STORM_SUMMARY_MANUAL_ROWS, ...STORM_SUMMARY_DESC_ALIASES].map(
      (entry) => [normalizeStormSummaryDesc(entry.desc), entry],
    ),
  );
  const eoQtys: Record<string, number> = {};

  rows.forEach((row) => {
    let matched: StormSummaryManualRow | undefined;
    for (let c = 0; c < row.length; c++) {
      if (c === STORM_SUMMARY_QUANT_COL) continue;
      matched = descLookup.get(normalizeStormSummaryDesc(row[c]));
      if (matched) break;
    }
    if (!matched) return;

    const rawQuant = row[matched.quantCol ?? STORM_SUMMARY_QUANT_COL];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') return;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) return; // Excel formula error
    const q = parseFloat(qStr);
    if (isNaN(q)) return;
    if (q > 0 || (matched.allowNegative && q < 0)) eoQtys[matched.key] = q;
  });

  return eoQtys;
};

/**
 * Import storm sewers data from Excel (reads 'Storm Manhole Schedule' and 'Storm Take off' sheets)
 */
export const importStormSewersFromExcel = async (
  file: File
): Promise<{
  data: StormSewersExcelData;
  errors: string[];
  warnings: string[];
}> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });

  const importedData: StormSewersExcelData = {
    manholes: [],
    pipeRuns: [],
    laterals: [],
    gullies: [],
    buildUp: {},
    roadConstConfig: undefined,
  };

  // ── 'Storm Manhole Schedule' — headers row 5 (index 4), data from row 8 (offset 3) ──
  const mhWs = workbook.Sheets['Storm Manhole Schedule'];
  if (mhWs) {
    const { rows } = readSheet(XLSX, mhWs, 4, 3);
    importedData.manholes = transformStormManholeSchedule(rows);

    // Read K8/L8 — global material type for ladders/safety chains (row 8, cols K=10, L=11)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row8Raw: unknown[][] = (XLSX as any).utils.sheet_to_json(mhWs, {
      header: 1, defval: null, raw: false,
      range: { s: { r: 7, c: 0 }, e: { r: 7, c: 20 } },
    });
    const row8 = (row8Raw[0] as unknown[]) || [];
    const laddersGlobal = resolveDropdown(row8[10]);  // column K
    const chainsGlobal  = resolveDropdown(row8[11]);  // column L
    if (laddersGlobal !== undefined || chainsGlobal !== undefined) {
      importedData.manholeScheduleConfig = {
        ladders: laddersGlobal ?? 'N/A',
        safetyChains: chainsGlobal ?? 'N/A',
      };
    }
  }

  // ── 'Storm Take off' — three side-by-side sections:
  //    Main Sewer Runs | House Laterals | Gully Laterals
  const toWs = workbook.Sheets['Storm Take off'];
  if (toWs) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allRows: unknown[][] = (XLSX as any).utils.sheet_to_json(toWs, {
      header: 1, defval: null, raw: false,
    });

    // The column-headers row ("Run ref", "Manhole From", ...) is found by
    // searching for its own text instead of a hardcoded absolute row index -
    // same fix and same reason as 'Foul Take off' in foulSewersExcelUtils.ts:
    // this sheet's layout has shifted by a row before (the `RS_ST_*`
    // validation key row and other rows above the real headers aren't a
    // fixed count across template revisions), and a hardcoded index silently
    // read the "Main Sewer Runs" section-title row instead, so every header
    // fell back to a meaningless `col_N` key and every field downstream read
    // as empty. Everything else in this block (the units row, the Road
    // Level/Formation Level/Road Construction Depth config cells, and the
    // first data row) is anchored relative to this header row rather than to
    // more absolute indices, since that internal block structure is a fixed
    // part of the sheet's own design and only the stuff *above* it grows.
    const headerRowIdx = allRows.findIndex(
      (row) => String((row as unknown[])?.[1] ?? '').trim().toLowerCase() === 'run ref',
    );
    if (headerRowIdx === -1) {
      console.warn("'Storm Take off': could not find the 'Run ref' header row - sheet layout may have changed.");
    }
    // headerRowIdx + 1 is a units row ("m", "mm", "Nr", ...) for every OTHER
    // column, but column G (index 6) repurposes that same row to start its
    // own 3-row Road Level/Formation Level/Road Construction Depth block
    // instead of a units row — confirmed against the real template
    // (public/R&S Take-off sheet.xlsx): Road Level sits at
    // headerRowIdx + 1, not + 2 (same off-by-one as 'Foul Take off' in
    // foulSewersExcelUtils.ts). Using the general +2 "units row" offset here
    // read Formation Level's cell into `roadLevel`, the calculated Depth
    // formula's cell into `formationLevel`, and the blank buffer row into
    // `depthCell` — silently wrong Road Const Depth on every import.
    const configRowIdx = headerRowIdx + 1;
    const roadLevelCell = toWs[XLSX.utils.encode_cell({ r: configRowIdx, c: 6 })];
    const formationLevelCell = toWs[XLSX.utils.encode_cell({ r: configRowIdx + 1, c: 6 })];
    const roadLevel = roadLevelCell ? toNum(roadLevelCell.v, 0) : 0;
    const formationLevel = formationLevelCell ? toNum(formationLevelCell.v, 0) : 0;
    // The Road Construction Depth cell itself (one row below Formation Level)
    // — a formula cell (`.f` set) means it's just "= road level − formation
    // level" (Calculated/Auto), so no override is needed; a plain typed-in
    // number (no `.f`) means the tender manually overrode it, matching the
    // modal's own Manual/Auto convention.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const depthCell = toWs[XLSX.utils.encode_cell({ r: configRowIdx + 2, c: 6 })] as any;
    const depthIsManual = !!depthCell && !depthCell.f;
    const depthValue = depthCell ? toNum(depthCell.v, 0) : 0;
    if (roadLevel !== 0 || formationLevel !== 0) {
      importedData.roadConstConfig = {
        roadLevel,
        formationLevel,
        ...(depthIsManual && depthValue !== 0 ? { depth: depthValue } : {}),
      };
    }

    const fullHeaders = ((allRows[headerRowIdx] ?? []) as unknown[]).map((h) => String(h ?? '').trim());

    // Find where House Laterals start — first lateral-specific header
    let lateralStartCol = fullHeaders.length;
    for (let i = 5; i < fullHeaders.length; i++) {
      const h = fullHeaders[i].toLowerCase();
      if (h.includes('manhole ref') || h.includes('demarc') || h === 'nme') {
        lateralStartCol = i;
        break;
      }
    }

    // Find where Gully Laterals start — null separator followed by non-null header after laterals
    let gullyStartCol = fullHeaders.length;
    for (let i = lateralStartCol + 1; i < fullHeaders.length - 1; i++) {
      if (fullHeaders[i] === '' && fullHeaders[i + 1] !== '') {
        gullyStartCol = i + 1;
        break;
      }
    }

    const mainHeaders    = fullHeaders.slice(0, lateralStartCol);
    const lateralHeaders = fullHeaders.slice(lateralStartCol, gullyStartCol);
    const gullyHeaders   = fullHeaders.slice(gullyStartCol);

    const stormSewerRows:   Record<string, unknown>[] = [];
    const stormLateralRows: Record<string, unknown>[] = [];
    const stormGullyRows:   Record<string, unknown>[] = [];

    // Main Sewer Runs, House Laterals, and Gully Laterals are three column
    // groups describing the SAME physical Excel row — downstream code
    // (combinedStormTakeOffData, getStormJunctionQty, calcStormLateralDerived,
    // calcStormGullyDerived, etc. in StormSewersTakeoff.tsx) matches
    // pipeRuns[i]/laterals[i]/gullies[i] by array index, so all three arrays
    // built here must stay in lockstep with the spreadsheet's row order. A
    // row is only dropped when ALL THREE groups are empty; otherwise every
    // group is pushed together (empty ones as a blank placeholder) — like a
    // left/right join keyed on row position rather than on any column value.
    // Data starts a few rows below the header (units row, Road Level,
    // Formation Level, Road Construction Depth, then a blank buffer row) -
    // anchored to `headerRowIdx` rather than a hardcoded absolute index for
    // the same reason as above. Starting one row early (at the blank buffer
    // row) is harmless since blank rows are skipped below anyway.
    for (let i = headerRowIdx + 4; i < allRows.length; i++) {
      const row = allRows[i] as unknown[];

      // Left columns → main sewer run
      const mainObj = Object.fromEntries(
        mainHeaders.map((h, ci) => [h || `col_${ci}`, row[ci] ?? null])
      );
      const hasMain = !isEmptyManholeRef(mainObj['Manhole From']) && !isEmptyManholeRef(mainObj['Manhole To']);
      mainObj['_hasMain'] = hasMain;

      // Middle columns → house lateral. Gated on the Manhole Ref column
      // specifically — not just whichever header happens to be first in this
      // section — so a blank Manhole Ref reliably means "no lateral on this
      // row" regardless of column order.
      let latObj: Record<string, unknown> = { _hasLateral: false };
      let hasLateral = false;
      if (lateralHeaders.length > 0) {
        latObj = Object.fromEntries(
          lateralHeaders.map((h, ci) => [h || `col_${lateralStartCol + ci}`, row[lateralStartCol + ci] ?? null])
        );
        hasLateral = !isEmptyManholeRef(col(latObj, 'Manhole Ref (mm)', 'Manhole Ref'));
        latObj['_rowBackfill'] = mainObj['Backfill'] ?? null;
        latObj['_hasLateral'] = hasLateral;
      }

      // Right columns → gully lateral. A gully connects onto this row's main
      // sewer run (Run Ref), so it also requires a valid Manhole From/To on
      // that run — without one there's no real run for the gully to attach to.
      // Gated on the Run Ref column specifically — not just whichever header
      // happens to be first in this section.
      let gullyObj: Record<string, unknown> = { _hasGully: false };
      let hasGully = false;
      if (gullyHeaders.length > 0) {
        gullyObj = Object.fromEntries(
          gullyHeaders.map((h, ci) => [h || `col_${gullyStartCol + ci}`, row[gullyStartCol + ci] ?? null])
        );
        hasGully = hasMain && !isPlaceholderRunRef(col(gullyObj, 'Run Ref'));
        gullyObj['_hasGully'] = hasGully;
      }

      if (!hasMain && !hasLateral && !hasGully) continue; // genuinely blank spreadsheet row

      stormSewerRows.push(mainObj);
      stormLateralRows.push(latObj);
      stormGullyRows.push(gullyObj);
    }

    importedData.pipeRuns = transformStormSewerRuns(stormSewerRows);
    importedData.laterals = transformStormLaterals(stormLateralRows);
    importedData.gullies  = transformStormGullies(stormGullyRows);
  } else {
    console.warn("'Storm Take off' sheet not found. Sheets:", workbook.SheetNames);
  }

  // ── 'Ponds & Swales' — desc col C(2), quant col H(7), evaluate code col I(8)
  const psWs = workbook.Sheets['Ponds & Swales'];
  if (psWs) {
    const psRows = XLSX.utils.sheet_to_json(psWs, {
      header: 1, defval: null, raw: false,
    }) as unknown[][];
    const psParsed = parsePondsSwalesSheet(psRows, 2, 7, 8);
    importedData.buildUp = {
      ...(importedData.buildUp || {}),
      ...psParsed.table,
    };
    if (Object.keys(psParsed.pondTopsoilThicknessMm).length > 0 || Object.keys(psParsed.swaleTopsoilThicknessMm).length > 0 || Object.keys(psParsed.sectionTitles).length > 0) {
      importedData.pondsSwalesConfig = {
        pondTopsoilThicknessMm: psParsed.pondTopsoilThicknessMm,
        swaleTopsoilThicknessMm: psParsed.swaleTopsoilThicknessMm,
        sectionTitles: psParsed.sectionTitles,
      };
    }
  }

  // ── 'Cellular Attenuation' — desc col B(1), quant col D(3), evaluate code col F(5)
  const caSheetName = workbook.SheetNames.find((n: string) => n.trim() === 'Cellular Attenuation');
  const caWs = caSheetName ? workbook.Sheets[caSheetName] : undefined;
  if (caWs) {
    const caRows = XLSX.utils.sheet_to_json(caWs, {
      header: 1, defval: null, raw: false,
    }) as unknown[][];
    const caParsed = parseCellularAttenuationSheet(caRows, 1, 3, 5);
    importedData.buildUp = {
      ...(importedData.buildUp || {}),
      ...caParsed.table,
    };
    if (Object.keys(caParsed.crateDims).length > 0) {
      importedData.cellularAttenuationConfig = { crateDims: caParsed.crateDims };
    }
  }

  // ── 'Weholite Attenuation' — desc col B(1), quant col F(5), evaluate code col H(7)
  const whSheetName = workbook.SheetNames.find((n: string) => n.trim() === 'Weholite Attenuation');
  const whWs = whSheetName ? workbook.Sheets[whSheetName] : undefined;
  if (whWs) {
    const whRows = XLSX.utils.sheet_to_json(whWs, {
      header: 1, defval: null, raw: false,
    }) as unknown[][];
    const whParsed = parseWeholiteAttenuationSheet(whRows, 1, 5, 7);
    importedData.buildUp = {
      ...(importedData.buildUp || {}),
      ...whParsed.table,
    };
    if (Object.keys(whParsed.weholiteConfigPct).length > 0) {
      importedData.weholiteAttenuationConfig = { weholiteConfigPct: whParsed.weholiteConfigPct };
    }
  }

  // ── 'Conc Attenuation' — desc col B(1), quant col C(2), evaluate code col E(4)
  const concSheetName = workbook.SheetNames.find((n: string) => n.trim() === 'Conc Attenuation');
  const concWs = concSheetName ? workbook.Sheets[concSheetName] : undefined;
  if (concWs) {
    const concRows = XLSX.utils.sheet_to_json(concWs, {
      header: 1, defval: null, raw: false,
    }) as unknown[][];
    const concParsed = parseConcAttenuationSheet(concRows, 1, 2, 4);
    importedData.buildUp = {
      ...(importedData.buildUp || {}),
      ...concParsed.table,
    };
    if (Object.keys(concParsed.concConfigPct).length > 0 || Object.keys(concParsed.drawingRefs).length > 0) {
      importedData.concAttenuationConfig = { concConfigPct: concParsed.concConfigPct, drawingRefs: concParsed.drawingRefs };
    }
  }

  // ── 'Box Culverts' — desc col C(2), quant col G(6), evaluate code col I(8)
  const bcSheetName = workbook.SheetNames.find((n: string) => n.trim() === 'Box Culverts');
  const bcWs = bcSheetName ? workbook.Sheets[bcSheetName] : undefined;
  if (bcWs) {
    const bcRows = XLSX.utils.sheet_to_json(bcWs, {
      header: 1, defval: null, raw: false,
    }) as unknown[][];
    const bcParsed = parseBoxCulvertsSheet(bcRows, 2, 6, 8);
    importedData.buildUp = {
      ...(importedData.buildUp || {}),
      ...bcParsed.table,
    };
    if (Object.keys(bcParsed.culvertConfigMm).length > 0 || Object.keys(bcParsed.culvertBackfillType).length > 0) {
      importedData.boxCulvertsConfig = {
        culvertConfigMm: bcParsed.culvertConfigMm,
        culvertBackfillType: bcParsed.culvertBackfillType,
      };
    }
  }

  // ── 'Storm Summary' sheet — manual EO/Redundant Sewer quantities (col I) ──
  const summaryWs = workbook.Sheets['Storm Summary'];
  if (summaryWs) {
    const summaryRows: unknown[][] = XLSX.utils.sheet_to_json(summaryWs, {
      header: 1, defval: null, raw: false, blankrows: false,
    });
    const eoQtys = parseStormSummarySheet(summaryRows);
    if (Object.keys(eoQtys).length > 0) importedData.eoQtys = eoQtys;
  } else {
    console.warn("'Storm Summary' sheet not found. Sheets:", workbook.SheetNames);
  }

  return { data: importedData, errors: [], warnings: [] };
};

/**
 * Validate imported data for basic consistency
 */
export const validateStormSewersData = (
  data: StormSewersExcelData
): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate manholes
  const manholeRefs = new Set<string>();
  data.manholes.forEach((manhole, index) => {
    if (!manhole.ref || manhole.ref.trim() === "") {
      errors.push(`Manhole ${index + 1}: Reference is required`);
    } else if (manholeRefs.has(manhole.ref)) {
      errors.push(`Manhole ${index + 1}: Duplicate reference "${manhole.ref}"`);
    } else {
      manholeRefs.add(manhole.ref);
    }

    if (manhole.coverLevel > 0 && manhole.invertLevel > 0 && manhole.coverLevel <= manhole.invertLevel) {
      warnings.push(
        `Manhole ${manhole.ref}: Cover level should be higher than invert level`
      );
    }
  });

  // Validate pipe runs against available manholes
  data.pipeRuns.forEach((pipeRun, index) => {
    if (!pipeRun.fromMH || !manholeRefs.has(pipeRun.fromMH)) {
      warnings.push(
        `Pipe Run ${index + 1}: From MH "${
          pipeRun.fromMH
        }" not found in manholes`
      );
    }
    if (!pipeRun.toMH || !manholeRefs.has(pipeRun.toMH)) {
      warnings.push(
        `Pipe Run ${index + 1}: To MH "${pipeRun.toMH}" not found in manholes`
      );
    }
    if (pipeRun.length <= 0) {
      warnings.push(`Pipe Run ${index + 1}: Length is 0 or not set`);
    }
  });

  // Validate laterals against available manholes
  data.laterals.forEach((lateral, index) => {
    if (lateral.connectionType === "direct_to_manhole") {
      if (!lateral.toManhole || !manholeRefs.has(lateral.toManhole)) {
        warnings.push(
          `Lateral ${index + 1}: To Manhole "${
            lateral.toManhole
          }" not found in manholes`
        );
      }
    } else if (lateral.connectionType === "junction_on_run") {
      if (!lateral.toPipeRun) {
        warnings.push(
          `Lateral ${
            index + 1
          }: To Pipe Run is required for junction connections`
        );
      }
    }
    if (lateral.length <= 0) {
      warnings.push(`Lateral ${index + 1}: Length is 0 or not set`);
    }
  });

  // Validate gullies against available pipe runs
  const pipeRunIds = new Set(data.pipeRuns.map((pr) => pr.id));
  data.gullies.forEach((gully, index) => {
    if (!gully.toPipeRun || !pipeRunIds.has(gully.toPipeRun)) {
      warnings.push(
        `Gully ${index + 1}: To Pipe Run "${
          gully.toPipeRun
        }" not found in pipe runs`
      );
    }
    if (gully.length <= 0) {
      warnings.push(`Gully ${index + 1}: Length is 0 or not set`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};
